import { c as _c } from "react-compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'hs-lodash/isEqual';
import omit from 'transmute/omit';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { useStore } from 'framework-listing-lib/internal/store/zustand';
import { UI_MODE } from 'framework-listing-lib/constants/uiMode';
import useCurrentView from 'framework-listing-lib/hooks/useCurrentView';
import { updateHiddenFilterForCalendarDisplayDate } from 'framework-listing-lib/internal/Calendar/utils/filters';
import { updateHiddenFilterForFolder } from 'framework-listing-lib/internal/Folders/utils/filters';
import { useCurrentViewId, useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { updateFilterGroupsForBUNav, updateHiddenFilterForBUNav, updateQuickFiltersForBUNav } from 'framework-listing-lib/internal/businessUnits/utils';
import useHandleQueryParams from 'framework-listing-lib/internal/deepLink/hooks/useHandleQueryParams';
import { useHasBUNavIntegration } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useDeepEqual from 'framework-listing-lib/internal/hooks/useDeepEqual';
import { useEventCallback } from 'framework-listing-lib/internal/hooks/useEventCallback';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { createFilterStore } from '../store/filterStateStore';
import { areSameFilterGroups, getAggregatedFilterGroups, removeFilterGroups } from 'framework-listing-lib/utils/filters';
import useSyncFiltersWithSessionStorage from './useSyncFiltersWithSessionStorage';
import { useCurrentUIMode } from './useUIMode';
import usePrevious from 'react-utils/hooks/usePrevious';
import { getViewId } from '../ViewTabs/utils/views';

/**
 * This hook is meant to be used only by CrmObjectTypeListingClientProvider.
 * It initialises the global filter state used by all hooks & components in this codebase.
 *
 * We update filters from this hook in two scenarios:
 *  - when @initialFilter or @objectType prop changes.
 *  - when the current view changes.
 *
 * This hooks has & controls:
 *  - list of deleted object ids (due to the delay with the CRM, we manually omit deleted objects when sending request to crm-search).
 *  - filters that have been applied to the UI
 */
export default function useCrmObjectTypeListingFilterState(t0) {
  "use memo";

  const $ = _c(85);
  const {
    calendarProps,
    hiddenFilter: hiddenFilterProp,
    initialFilter: initialFilterProp,
    folderId
  } = t0;
  const urlParams = useHandleQueryParams();
  const currentUIMode = useCurrentUIMode();
  const hasViews = useHasViews();
  const currentView = useCurrentView();
  const currentViewId = useCurrentViewId();
  const initialFilter = useDeepEqual(initialFilterProp);
  const hasBUNavIntegration = useHasBUNavIntegration();
  const objectType = useObjectTypeId();
  let t1;
  if ($[0] !== currentUIMode || $[1] !== currentView || $[2] !== initialFilter) {
    t1 = () => {
      const store = createFilterStore();
      store.getState().initializeFiltersState({
        currentView,
        uiMode: currentUIMode,
        initialFilter
      });
      return store;
    };
    $[0] = currentUIMode;
    $[1] = currentView;
    $[2] = initialFilter;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [filtersStateStore] = useState(t1);
  const t2 = useStore(filtersStateStore, _temp);
  const t3 = useStore(filtersStateStore, _temp2);
  const t4 = !useStore(filtersStateStore, _temp3);
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      filterGroups: t2,
      quickFilters: t3,
      skip: t4
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const filterStateFromSessionStorage = useSyncFiltersWithSessionStorage(t5);
  const [prevViewId, setPrevViewId] = useState(undefined);
  let t6;
  if ($[8] !== currentUIMode || $[9] !== currentView || $[10] !== currentViewId || $[11] !== (filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups) || $[12] !== (filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters) || $[13] !== filtersStateStore || $[14] !== initialFilter || $[15] !== objectType || $[16] !== prevViewId) {
    t6 = () => {
      var _filterStateFromSessi, _filterStateFromSessi2;
      if (currentUIMode === UI_MODE.FOLDERS || !currentView) {
        setPrevViewId(undefined);
        return;
      }
      const viewId = getViewId(currentView);
      if (prevViewId === viewId) {
        return;
      }
      setPrevViewId(viewId);
      const resolvedView = !currentView || !currentViewId || objectType !== currentView.objectTypeId ? undefined : Object.assign({}, currentView, {
        filterGroups: (_filterStateFromSessi = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups) !== null && _filterStateFromSessi !== void 0 ? _filterStateFromSessi : currentView === null || currentView === void 0 ? void 0 : currentView.filterGroups,
        quickFilters: (_filterStateFromSessi2 = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters) !== null && _filterStateFromSessi2 !== void 0 ? _filterStateFromSessi2 : currentView === null || currentView === void 0 ? void 0 : currentView.quickFilters
      });
      filtersStateStore.getState().initializeFiltersState({
        currentView: resolvedView,
        uiMode: currentUIMode,
        initialFilter
      });
    };
    $[8] = currentUIMode;
    $[9] = currentView;
    $[10] = currentViewId;
    $[11] = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups;
    $[12] = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters;
    $[13] = filtersStateStore;
    $[14] = initialFilter;
    $[15] = objectType;
    $[16] = prevViewId;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups;
  const t8 = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters;
  let t9;
  if ($[18] !== currentUIMode || $[19] !== currentView || $[20] !== currentViewId || $[21] !== filtersStateStore || $[22] !== initialFilter || $[23] !== objectType || $[24] !== prevViewId || $[25] !== t7 || $[26] !== t8) {
    t9 = [currentUIMode, currentView, currentViewId, t7, t8, filtersStateStore, initialFilter, objectType, prevViewId];
    $[18] = currentUIMode;
    $[19] = currentView;
    $[20] = currentViewId;
    $[21] = filtersStateStore;
    $[22] = initialFilter;
    $[23] = objectType;
    $[24] = prevViewId;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  useEffect(t6, t9);
  const prevUIMode = usePrevious(currentUIMode);
  let t10;
  let t11;
  if ($[28] !== currentUIMode || $[29] !== filtersStateStore || $[30] !== initialFilter || $[31] !== prevUIMode) {
    t10 = () => {
      if (prevUIMode !== currentUIMode && currentUIMode === UI_MODE.FOLDERS) {
        filtersStateStore.getState().initializeFiltersState({
          currentView: undefined,
          uiMode: currentUIMode,
          initialFilter
        });
      }
    };
    t11 = [currentUIMode, filtersStateStore, initialFilter, prevUIMode];
    $[28] = currentUIMode;
    $[29] = filtersStateStore;
    $[30] = initialFilter;
    $[31] = prevUIMode;
    $[32] = t10;
    $[33] = t11;
  } else {
    t10 = $[32];
    t11 = $[33];
  }
  useEffect(t10, t11);
  const prevFolderIdRef = useRef(folderId);
  let t12;
  let t13;
  if ($[34] !== filtersStateStore || $[35] !== folderId) {
    t12 = () => {
      if (prevFolderIdRef.current === folderId) {
        return;
      }
      prevFolderIdRef.current = folderId;
      filtersStateStore.getState().resetPaginationOffset();
    };
    t13 = [folderId, filtersStateStore];
    $[34] = filtersStateStore;
    $[35] = folderId;
    $[36] = t12;
    $[37] = t13;
  } else {
    t12 = $[36];
    t13 = $[37];
  }
  useEffect(t12, t13);
  let t14;
  if ($[38] !== currentUIMode || $[39] !== filtersStateStore) {
    t14 = effectEvent => {
      filtersStateStore.getState().updateInitialFilters({
        initialFilter: effectEvent.initialFilter,
        uiMode: currentUIMode
      });
    };
    $[38] = currentUIMode;
    $[39] = filtersStateStore;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const handleInitialFilterChange = useEventCallback(t14);
  let t15;
  if ($[41] !== filtersStateStore) {
    t15 = effectEvent_0 => {
      filtersStateStore.getState().setQuery(effectEvent_0.query);
    };
    $[41] = filtersStateStore;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const handleQueryChange = useEventCallback(t15);
  const initialFilterRef = useRef(initialFilter);
  let t16;
  let t17;
  if ($[43] !== handleInitialFilterChange || $[44] !== handleQueryChange || $[45] !== initialFilter) {
    t16 = () => {
      const hasInitialFilter = Boolean(initialFilterRef.current && initialFilter);
      const nonQueryFiltersAreEqual = isEqual(omit(["query"], initialFilterRef.current || {}), omit(["query"], initialFilter || {}));
      const queryIsDifferent = Boolean(initialFilterRef.current && initialFilter && initialFilterRef.current.query !== initialFilter.query);
      const hasOnlyQueryChanged = hasInitialFilter && nonQueryFiltersAreEqual && queryIsDifferent;
      if (hasOnlyQueryChanged) {
        var _initialFilter$query;
        handleQueryChange({
          query: (_initialFilter$query = initialFilter === null || initialFilter === void 0 ? void 0 : initialFilter.query) !== null && _initialFilter$query !== void 0 ? _initialFilter$query : ""
        });
      } else {
        if (!nonQueryFiltersAreEqual) {
          handleInitialFilterChange({
            initialFilter
          });
        }
      }
      initialFilterRef.current = initialFilter;
    };
    t17 = [handleInitialFilterChange, handleQueryChange, initialFilter];
    $[43] = handleInitialFilterChange;
    $[44] = handleQueryChange;
    $[45] = initialFilter;
    $[46] = t16;
    $[47] = t17;
  } else {
    t16 = $[46];
    t17 = $[47];
  }
  useEffect(t16, t17);
  let t18;
  if ($[48] !== currentUIMode || $[49] !== currentView || $[50] !== filtersStateStore || $[51] !== initialFilter) {
    t18 = effectEvent_1 => {
      filtersStateStore.getState().consumeQueryParams({
        urlParams: effectEvent_1.urlParams,
        currentView,
        uiMode: currentUIMode,
        initialFilter
      });
    };
    $[48] = currentUIMode;
    $[49] = currentView;
    $[50] = filtersStateStore;
    $[51] = initialFilter;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  const handleConsumeQueryParams = useEventCallback(t18);
  let t19;
  let t20;
  if ($[53] !== currentView || $[54] !== filtersStateStore || $[55] !== handleConsumeQueryParams || $[56] !== hasViews || $[57] !== urlParams) {
    t19 = () => {
      const isLoadingOrInvalidView = hasViews && !currentView;
      if (isLoadingOrInvalidView || !urlParams || filtersStateStore.getState().hasUsedQueryParams) {
        return;
      }
      handleConsumeQueryParams({
        urlParams
      });
    };
    t20 = [currentView, filtersStateStore, handleConsumeQueryParams, hasViews, urlParams];
    $[53] = currentView;
    $[54] = filtersStateStore;
    $[55] = handleConsumeQueryParams;
    $[56] = hasViews;
    $[57] = urlParams;
    $[58] = t19;
    $[59] = t20;
  } else {
    t19 = $[58];
    t20 = $[59];
  }
  useEffect(t19, t20);
  let t21;
  if ($[60] !== hasBUNavIntegration || $[61] !== (hiddenFilterProp === null || hiddenFilterProp === void 0 ? void 0 : hiddenFilterProp.filterGroups)) {
    t21 = state => {
      const filterGroupsWithoutHiddenFilter = removeFilterGroups(state.filtersState.filterGroups, (hiddenFilterProp === null || hiddenFilterProp === void 0 ? void 0 : hiddenFilterProp.filterGroups) || []);
      const filterGroupsWithBUNav = updateFilterGroupsForBUNav({
        filterGroups: filterGroupsWithoutHiddenFilter,
        hasBUNavIntegration
      });
      return filterGroupsWithBUNav;
    };
    $[60] = hasBUNavIntegration;
    $[61] = hiddenFilterProp === null || hiddenFilterProp === void 0 ? void 0 : hiddenFilterProp.filterGroups;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  const filterGroups = useStoreWithEqualityFn(filtersStateStore, t21, shallow);
  let t22;
  if ($[63] !== hasBUNavIntegration) {
    t22 = state_0 => updateQuickFiltersForBUNav({
      quickFilters: state_0.filtersState.quickFilters,
      hasBUNavIntegration
    });
    $[63] = hasBUNavIntegration;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  const quickFilters = useStoreWithEqualityFn(filtersStateStore, t22, shallow);
  let t23;
  if ($[65] !== calendarProps || $[66] !== currentUIMode || $[67] !== folderId || $[68] !== hasBUNavIntegration || $[69] !== hiddenFilterProp) {
    bb0: {
      const withHiddenBUFilter = updateHiddenFilterForBUNav({
        hiddenFilter: hiddenFilterProp,
        hasBUNavIntegration
      });
      const withHiddenFolderFilter = updateHiddenFilterForFolder({
        folderId,
        hiddenFilter: withHiddenBUFilter,
        isFoldersMode: currentUIMode === UI_MODE.FOLDERS
      });
      if (!calendarProps || currentUIMode !== UI_MODE.CALENDAR) {
        t23 = withHiddenFolderFilter;
        break bb0;
      }
      t23 = updateHiddenFilterForCalendarDisplayDate({
        displayTimestamp: calendarProps.displayTimestamp,
        endPropertyName: calendarProps.endPropertyName,
        hiddenFilter: withHiddenFolderFilter,
        startPropertyName: calendarProps.startPropertyName,
        viewType: calendarProps.use
      });
    }
    $[65] = calendarProps;
    $[66] = currentUIMode;
    $[67] = folderId;
    $[68] = hasBUNavIntegration;
    $[69] = hiddenFilterProp;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  const hiddenFilter = t23;
  const deletedObjects = useStore(filtersStateStore, _temp4);
  let t24;
  bb1: {
    if (!currentView) {
      t24 = false;
      break bb1;
    }
    let t25;
    if ($[71] !== currentView.filterGroups || $[72] !== currentView.quickFilters || $[73] !== deletedObjects || $[74] !== filterGroups || $[75] !== hasBUNavIntegration || $[76] !== hiddenFilter || $[77] !== quickFilters) {
      const now = Date.now();
      const getNow = () => now;
      const currentAggregatedFilterGroups = getAggregatedFilterGroups({
        deletedObjects,
        filterGroups,
        hiddenFilter,
        includeHiddenFilter: true,
        quickFilters,
        getNow
      });
      const viewAggregatedFilterGroups = getAggregatedFilterGroups({
        deletedObjects,
        filterGroups: updateFilterGroupsForBUNav({
          filterGroups: currentView.filterGroups,
          hasBUNavIntegration
        }),
        hiddenFilter,
        includeHiddenFilter: true,
        quickFilters: updateQuickFiltersForBUNav({
          quickFilters: currentView.quickFilters,
          hasBUNavIntegration
        }),
        getNow
      });
      t25 = areSameFilterGroups(currentAggregatedFilterGroups, viewAggregatedFilterGroups);
      $[71] = currentView.filterGroups;
      $[72] = currentView.quickFilters;
      $[73] = deletedObjects;
      $[74] = filterGroups;
      $[75] = hasBUNavIntegration;
      $[76] = hiddenFilter;
      $[77] = quickFilters;
      $[78] = t25;
    } else {
      t25 = $[78];
    }
    t24 = t25;
  }
  const isFilterFromCustomView = t24;
  let t25;
  if ($[79] !== filterGroups || $[80] !== filtersStateStore || $[81] !== hiddenFilter || $[82] !== isFilterFromCustomView || $[83] !== quickFilters) {
    t25 = {
      filterGroups,
      filtersStateStore,
      hiddenFilter,
      isFilterFromCustomView,
      quickFilters
    };
    $[79] = filterGroups;
    $[80] = filtersStateStore;
    $[81] = hiddenFilter;
    $[82] = isFilterFromCustomView;
    $[83] = quickFilters;
    $[84] = t25;
  } else {
    t25 = $[84];
  }
  const value = t25;
  return value;
}
function _temp4(state_1) {
  return state_1.deletedObjects;
}
function _temp3(s_1) {
  return s_1.shouldSyncWithSessionStorage;
}
function _temp2(s_0) {
  return s_0.filtersState.quickFilters;
}
function _temp(s) {
  return s.filtersState.filterGroups;
}